/** @module Auth0SmsCollectionHelper */
/* eslint-disable import/extensions */
import { Auth0PageBase } from './auth0-page-template.mst.mjs';
import SmsCollectionHtml from '../../../public/smsCollectionPage.html.mst';
import CountryCodeSelect from '../../../public/country-code-select.html.mst';
import Auth0Cdn from '../utils/auth0-cdn-resource-resolver.mst.mjs';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import Auth0MfaCodePageHelper from './auth0-mfa-code-page-helper.mst.mjs';
import Auth0MfaChoicePageHelper from './auth0-mfa-choice-page-helper.mst.mjs';
import Auth0Constants from '../../utils/auth0-constants.mjs';
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';

class Auth0SmsCollectionHelper extends Auth0PageBase {
  constructor(options) {
    super(options);

    this._pageStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-sms-collection-styles.css?cb=71c5d67`,
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-mfa-styles.css?cb=71c5d67`,
    ];

    this._uriState = options.uriState;
    this._grantToken = options.grantToken;
    this._options = options;
    this._userMfaChoiceRequired = options.userMfaChoiceRequired;
  }

  get _grantTokenContinueUrl() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._grantToken)?.continueUrl;
  }

  get _startButton() {
    return document.getElementById('start-enrollment-button');
  }

  get _optOutButton() {
    return document.getElementById('sms-do-later-link');
  }

  get _countryCodeSelect() {
    return document.getElementById('country-code-select');
  }

  get _smsInput() {
    return document.getElementById('sms-input');
  }

  get _grantTokenTriggerIds() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._grantToken)?.triggerIds;
  }

  _disableButtons(buttonsToUpdate) {
    if (this._startButton) {
      this._disableButton(this._startButton, 'please-wait-generic');
    }

    if (buttonsToUpdate) {
      buttonsToUpdate.forEach((button) => {
        this._disableButton(button, 'please-wait-generic');
      });
    }
  }

  _enableButtons(buttonsToUpdate) {
    if (this._startButton) {
      this._enableButton(this._startButton, 'mfa-enrollment-start');
    }

    if (buttonsToUpdate) {
      buttonsToUpdate.forEach((button) => {
        this._enableButton(button, 'please-wait-generic');
      });
    }
  }

  async _onOptOut(buttonsToUpdate) {
    try {
      this._disableButtons(buttonsToUpdate);
      const needsMfa = this._grantTokenTriggerIds?.length;
      if (needsMfa) {
        new Auth0MfaCodePageHelper({
          mfaFactorType: Auth0Constants.MFA_OOB_CHANNEL_EMAIL,
          mfaGrantToken: this._grantToken,
          mfaState: this._uriState,
          name: 'Auth0MfaCodePageHelper',
          tenantOrDomain: this._options.tenantOrDomain,
          whiteLabelOptions: this._options.whiteLabelOptions,
          appLoginUrl: this._options.appLoginUrl,
          clientId: this._options.clientId,
        }).render();
        return;
      }

      const resBody = await this._auth0ManagementApi.optOutOfSmsEnrollment({
        state: this._uriState,
        mfaGrantToken: this._grantToken,
      });
      const url = `${this._grantTokenContinueUrl}?${new URLSearchParams({
        state: this._uriState,
        context: resBody.amfaContext,
      })}`;

      document.location = url;
    } catch (error) {
      this._enableButtons(buttonsToUpdate);
      this._hideInfo();
      this._displayError('error-mfa-sms-opt-out', { code: Auth0Constants.MFA_ERROR_CODE_OPT_OUT_FAIL, supportEmail: this._whiteLabelHelper.appLoginSupportEmail, supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
    }
  }

  async _onExistingFactorVerify(existingFactorOptions, phone) {
    const enrollmentOptions = {
      state: this._uriState,
      mfaGrantToken: this._grantToken,
      existingFactorOptions,
      type: Auth0Constants.MFA_TYPE_OOB,
      oobChannel: Auth0Constants.MFA_OOB_CHANNEL_SMS,
      phone,
    };
    const enrollmentResult = await this._auth0ManagementApi.enrollAdditionalMfaFactor(enrollmentOptions);
    this.verifyMfaToken = enrollmentResult.mfaToken;
    return enrollmentResult;
  }

  async _onResendNewEnrollmentChallenge(challengeOptions, phone) {
    const options = {
      type: Auth0Constants.MFA_TYPE_OOB,
      oobChannel: Auth0Constants.MFA_OOB_CHANNEL_SMS,
      phone,
      mfaToken: this.verifyMfaToken,
      state: challengeOptions.state,
      mfaGrantToken: challengeOptions.mfaGrantToken,
    };
    // this needs to match the shape of autoEnrollOrChallengeAmfaFactor response
    const newChallenge = await this._auth0ManagementApi.resendNewEnrollmentChallenge(options);
    console.log('this should match the autoEnrollOrchallengeAmfaFactor shape', newChallenge);
    return newChallenge;
  }

  async _onEnrollmentContinueToSmsVerification(smsPromptResponse, phone) {
    const smsChallenge = {
      oobCode: smsPromptResponse.oob_code,
      mfaToken: this.verifyMfaToken,
      amfaContext: smsPromptResponse.amfaContext,
    };

    new Auth0MfaCodePageHelper({
      mfaFactorType: Auth0Constants.MFA_OOB_CHANNEL_SMS,
      mfaGrantToken: this._grantToken,
      mfaState: this._uriState,
      name: 'Auth0MfaCodePageHelper',
      tenantOrDomain: this._options.tenantOrDomain,
      whiteLabelOptions: this._options.whiteLabelOptions,
      appLoginUrl: this._options.appLoginUrl,
      clientId: this._options.clientId,
      currentChallenge: smsChallenge,
      userPhone: phone,
      sendVerificationCode: challengeOptions => this._onResendNewEnrollmentChallenge(challengeOptions, phone),
      onOptOut: buttonsToUpdate => this._onOptOut(buttonsToUpdate),
      titleResourceId: 'mfa-additional-enrollment-sms-title',
    }).render();
  }

  _renderMfaCodeChallenge(phone, factor) {
    const sendVerificationCode = async (challengeOptions) => {
      const body = {
        ...challengeOptions,
        factor,
      };
      const res = await this._auth0ManagementApi.getEnrollMfaTokenForMfaChallenge(body);
      this.verifyMfaToken = res.mfaToken;
      const mappedRes = {
        ...res,
        amfaContext: res.challengeContextToken,
      };
      return mappedRes;
    };

    let mfaFactorType = Auth0Constants.MFA_OOB_CHANNEL_EMAIL;
    if (factor) {
      [mfaFactorType] = factor.id.split('|');
    }

    const getTitleResourceId = () => {
      if (mfaFactorType === Auth0Constants.MFA_TYPE_TOTP) {
        return 'mfa-additional-enrollment-totp-initial-challenge-title';
      }


      return 'mfa-additional-enrollment-initial-challenge-title';
    };

    new Auth0MfaCodePageHelper({
      mfaFactorType,
      mfaGrantToken: this._grantToken,
      mfaState: this._uriState,
      name: 'Auth0MfaCodePageHelper',
      tenantOrDomain: this._options.tenantOrDomain,
      whiteLabelOptions: this._options.whiteLabelOptions,
      appLoginUrl: this._options.appLoginUrl,
      clientId: this._options.clientId,
      onContinue: (smsPromptResponse) => { this._onEnrollmentContinueToSmsVerification(smsPromptResponse, phone); },
      onVerify: async existingFactorVerifyOptions => this._onExistingFactorVerify(existingFactorVerifyOptions, phone),
      sendVerificationCode,
      onOptOut: buttonsToUpdate => this._onOptOut(buttonsToUpdate),
      titleResourceId: getTitleResourceId(),
      preambleResourceId: 'mfa-additional-enrollment-initial-challenge-preamble',
      shouldHideSendCode: mfaFactorType === Auth0Constants.MFA_TYPE_TOTP,
      sentToValue: mfaFactorType === Auth0Constants.MFA_TYPE_TOTP
        ? ''
        : factor?.name,
    }).render();
  }

  async _onStartSmsEnrollment() {
    this._disableButtons();
    this._countryCodeSelect.disabled = true;
    this._smsInput.disabled = true;
    const getAllDigits = (sms) => {
      if (!sms) {
        return null;
      }
      const allDigitMatches = sms.match(/\d{1,}/g);
      if (!allDigitMatches) {
        return null;
      }
      const allDigits = allDigitMatches.join('');
      return allDigits;
    };
    const getPhoneNumber = (country, sms) => {
      const allDigits = getAllDigits(sms);
      return `+${country}${allDigits}`;
    };

    const countryCode = this._countryCodeSelect.value;
    if (!countryCode) {
      this._enableButtons();
      this._countryCodeSelect.disabled = false;
      this._smsInput.disabled = false;
      this._displayError('Invalid country code. Please enter a valid country code');
    }

    const smsNum = getAllDigits(this._smsInput.value);
    if (!smsNum) {
      this._enableButtons();
      this._countryCodeSelect.disabled = false;
      this._smsInput.disabled = false;
      this._displayError('Invalid Sms Number. Please enter a valid phone number');
    }

    const phone = getPhoneNumber(countryCode, smsNum);
    if (!this._userMfaChoiceRequired) {
      this._renderMfaCodeChallenge(phone);
      return;
    }
    new Auth0MfaChoicePageHelper({
      mfaGrantToken: this._options.grantToken,
      mfaState: this._options.uriState,
      name: 'Auth0MfaCodePageHelper',
      tenantOrDomain: this._options.tenantOrDomain,
      whiteLabelOptions: this._options.whiteLabelOptions,
      appLoginUrl: this._options.appLoginUrl,
      clientId: this._options.clientId,
      onFactorSelected: (factor) => {
        this._renderMfaCodeChallenge(phone, factor);
      },
    }).render();
  }

  _cancelMfaAndLogout = () => {
    this._logout();
  };

  _localize = async () => {
    await super._localize();

    Auth0Localizer.setElementLocalizedText({ id: 'enroll-title', resourceIdOverride: 'mfa-enrollment-start-title' });
    Auth0Localizer.setElementLocalizedText({ id: 'enroll-preamble', resourceIdOverride: 'mfa-enrollment-preamble' });
    Auth0Localizer.setElementLocalizedText({ id: 'enroll-country-label', resourceIdOverride: 'mfa-enrollment-sms-country' });
    Auth0Localizer.setElementLocalizedText({ id: 'enroll-sms-label', resourceIdOverride: 'mfa-enrollment-sms-number' });
    Auth0Localizer.setElementLocalizedText({ id: 'start-enrollment-button', resourceIdOverride: 'mfa-enrollment-start' });
    Auth0Localizer.setElementLocalizedText({ id: 'sms-do-later-link', resourceIdOverride: 'mfa-enrollment-decline' });
    Auth0Localizer.setElementLocalizedText({ id: 'mfa-footer-1', resourceIdOverride: 'mfa-enrollment-footer-text-1' });
    Auth0Localizer.setElementLocalizedText({ id: 'mfa-footer-2', resourceIdOverride: 'mfa-enrollment-footer-text-2' }, { supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
  };

  _initSmsCollectionHtml = async () => {
    await this._localize();
    await this._applyWhiteLabel(this._options.whiteLabelOptions);

    this._startButton.onclick = () => { this._onStartSmsEnrollment(); };
    this._optOutButton.onclick = () => { this._onOptOut(); };

    this._smsInput.focus();
  };

  async _onLoad() {
    await super._onLoad();
    this.toolbarVisible = true;
    this.actionLinkVisible = true;
    this.actionLinkText = Auth0Localizer.getLocalizedText('logout-link-text');
    this.actionLinkTextHandler = this._cancelMfaAndLogout;
    await this.setBody(SmsCollectionHtml);
    const countryCodeHtml = document.getElementById('country-code-wrapper').innerHTML;
    document.getElementById('country-code-wrapper').innerHTML = `${countryCodeHtml}${CountryCodeSelect}`;
  }

  async _startUi() {
    console.log('sms: _startUi');
    await super._startUi();
    await this._initSmsCollectionHtml();
  }

  render(forceRerender = false) {
    // what, if anything, to do on page refresh?
    super.render(forceRerender);
  }
}

export default Auth0SmsCollectionHelper;
